import type { useLocalization } from "@lemmonhq/uikit";

type Translate = ReturnType<typeof useLocalization>["t"];

// Stable machine refusal codes raised by the membership reservation/capture SQL
// (payment_resource_holds_prepare / membership_usage_capture), mapped to
// translated checkout messages so a capped-reward refusal never surfaces as a
// raw Postgres error in a toast.
// See docs/plans/loyalty/phase_1_capped_reward_checkout.md.
const REFUSAL_KEYS: Record<string, string> = {
  membership_quota_exhausted: "loyalty.checkout.quota_exhausted",
  membership_budget_exhausted: "loyalty.checkout.budget_exhausted",
  membership_not_a_member: "loyalty.checkout.not_a_member",
  membership_inactive: "loyalty.checkout.inactive",
  membership_not_applicable: "loyalty.checkout.not_applicable",
  membership_program_disabled: "loyalty.checkout.program_disabled",
  membership_hold_missing: "loyalty.checkout.hold_missing",
};

function extractErrorText(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  const e = error as {
    message?: string;
    shape?: { message?: string };
    data?: { message?: string };
    cause?: { message?: string };
  };
  return [e.message, e.shape?.message, e.data?.message, e.cause?.message]
    .filter(Boolean)
    .join(" ");
}

/**
 * If `error` carries a known membership refusal code, return the translated
 * message for it; otherwise null (so the caller falls back to its default).
 */
export function membershipRefusalMessage(
  error: unknown,
  t: Translate,
): string | null {
  const text = extractErrorText(error);
  if (!text) return null;
  for (const [code, key] of Object.entries(REFUSAL_KEYS)) {
    if (text.includes(code)) return t(key);
  }
  return null;
}
